<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="5">
        <div class="h3">
          <h4>
            A model never perfectly reflects reality, which will result in a
            certain risk when you will take your final decision.
          </h4>
          <div>
            <h4>The risk you can accept must reflect:</h4>
            <li>the decision consequence</li>
            <li>the model influence on your decision</li>
          </div>
          <h4>
            Make sure the model you choose does not exceed the risk you can
            accept.
          </h4>
        </div>
      </v-col>
      <v-col cols="7">
        <v-row class="center-y" no-gutters>
          <v-col cols="2">
            <div class="axis-y">Decision Consequence</div>
          </v-col>
          <v-col cols="10">
            <div class="square-container">
              <div class="square" @click="handleClick">
                <div class="diagonal"></div>
                <div
                  class="selection"
                  :style="{
                    top: mouseY + '%',
                    left: mouseX + '%',
                    width: selectionSize + 'px',
                    height: selectionSize + 'px',
                    borderRadius: selectionSize / 2 + 'px',
                    transform: 'translate(-50%, -50%)',
                  }"
                ></div>
              </div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="axis-x">Model Influence</div>
            <div class="risk-display">Risk defined</div>
            <div class="risk-display">{{ risk }}</div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiClient from "@/plugins/apiService";
export default {
  props: ["selectedProblem"],
  data() {
    return {
      mouseX: 0,
      mouseY: 0,
      risk: "",
      selectionSize: 0,
      selectedEstimation: "Quantitative",
      needData: null, // Stores response data from request
      selectedObjectives: [], // Stores selected objectives
      objectives: [], // Stores the objectives received from the server
    };
  },
  mounted() {
    this.fetchNeedData(); // Call the function to get need data
    this.fetchObjectives(); // Call the function to get objectives
  },
  watch: {
    needData: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.selectedEstimation = newValue.estimation_type;

          if (newValue.accepted_risk) {
            const mousePos = newValue.accepted_risk
              .split(",")
              .map((coord) => parseInt(coord.split(":")[1]));
            this.mouseX = mousePos[0];
            this.mouseY = mousePos[1];
          } else {
            this.mouseX = 0;
            this.mouseY = 0;
          }

          this.updateRisk();
          this.updateSelectionSize();

          // Update the selected objectives
          if (newValue.objectives) {
            this.selectedObjectives = newValue.objectives
              .split(",")
              .map((obj) => obj.trim());
          } else {
            this.selectedObjectives = [];
          }
        }
      },
    },
    selectedEstimation: "updateProblem",
    selectedObjectives: "updateProblem",
    mouseX: "updateProblem",
    mouseY: "updateProblem",
  },
  methods: {
    // Function to get the need data
    async fetchNeedData() {
      try {
        const response = await apiClient.get(`/need/${this.selectedProblem}`);
        const data = response.data;
        this.needData = data[0]; // Ensure you access the first object in the array
      } catch (error) {
        console.error("Error fetching need data:", error);
      }
    },
    // Function to get objectives from the backend
    async fetchObjectives() {
      try {
        const response = await apiClient.get(`/objective`);
        const data = response.data;
        this.objectives = data; // Assign the received objectives to the objectives variable
      } catch (error) {
        console.error("Error fetching objectives:", error);
      }
    },
    handleClick(event) {
      const rect = event.target.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      this.mouseX = Math.round((x / rect.width) * 100);
      this.mouseY = Math.round((y / rect.height) * 100);

      this.updateRisk();
      this.updateSelectionSize();
    },
    updateRisk() {
      this.risk = this.calculateRisk();
    },
    updateSelectionSize() {
      const squareContainer = this.$el.querySelector(".square-container");
      if (squareContainer) {
        const rect = squareContainer.getBoundingClientRect();
        this.selectionSize = rect.width * 0.1;
      }
    },
    calculateRisk() {
      const total = this.mouseX + (100 - this.mouseY);
      if (total <= 66) {
        return "LOW";
      } else if (total <= 132) {
        return "MEDIUM";
      } else {
        return "HIGH";
      }
    },
    // New function to update the problem
    async updateProblem() {
      try {
        const updateData = {
          estimation_type: this.selectedEstimation,
          accepted_risk: `mouseX: ${this.mouseX}, mouseY: ${this.mouseY}`,
          objectives: this.selectedObjectives.join(", "),
        };

        const response = await apiClient.put(
          `/need/${this.selectedProblem}`,
          updateData
        );

        if (response.status === 200) {
          console.log("Problem updated successfully");
        } else {
          console.error("Failed to update the problem");
        }
      } catch (error) {
        console.error("Error updating the problem:", error);
      }
    },
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.h3 {
  margin-top: 20px;
  text-align: left;
  line-height: 1.7;
  background: var(--secondaryColorLighter);
  padding: 20px 24px;
  border-radius: 12px;
  border-left: 4px solid var(--primaryColor);
}

.h3 h4 {
  color: var(--darkColor);
  font-weight: 500;
  margin-bottom: 12px;
}

.h3 li {
  color: var(--darkColor);
  margin-bottom: 8px;
  margin-left: 20px;
}

.checklist {
  margin-right: 20px;
}

.square-container {
  width: 350px;
  height: 350px;
  position: relative;
  background: linear-gradient(
    135deg,
    var(--secondaryColorLighter) 0%,
    var(--secondaryColorLight) 100%
  );
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 2px solid var(--secondaryColor);
}

.square {
  width: 100%;
  height: 100%;
  position: relative;
  cursor: crosshair;
}

.diagonal {
  width: 100%;
  height: 100%;
  position: absolute;
  background: linear-gradient(
    to bottom left,
    rgba(143, 39, 125, 0.25),
    transparent
  );
}

.selection {
  position: absolute;
  background-color: var(--primaryColor);
  opacity: 0.7;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(95, 151, 122, 0.4);
  transition: all 0.15s ease;
}

.axis-x {
  width: 100%;
  height: 24px;
  line-height: 24px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--primaryColor);
  margin-top: 12px;
}

.axis-y {
  width: 100%;
  text-align: center;
  transform: translateY(-50%) rotate(-90deg);
  font-size: 13px;
  font-weight: 600;
  color: var(--primaryColor);
}

.risk-display {
  width: 100%;
  text-align: center;
  margin-top: 12px;
  font-size: 16px;
  font-weight: 600;
  color: var(--contrastColor);
  padding: 8px 16px;
  background: rgba(143, 39, 125, 0.1);
  border-radius: 8px;
  display: inline-block;
}

.center-y {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
